import React, { useEffect, useRef, useState } from "react";
import type { LibraryLinkOption } from "../../API/Library/LibraryCommands";
import { CheckIcon, ChevronIcon } from "./LibraryIcons";
import styles from "./Library.module.css";

export const NO_LINK = "none";

export const linkKey = (option: LibraryLinkOption) => `${option.type}:${option.id}`;

type LibraryLinkPickerProps = {
  options: LibraryLinkOption[];
  value: string;
  required: boolean;
  partnerLabel: string;
  onChange: (value: string) => void;
};

const LibraryLinkPicker = ({ options, value, required, partnerLabel, onChange }: LibraryLinkPickerProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const selected = options.find((option) => linkKey(option) === value);
  const typeLabel = (type: LibraryLinkOption["type"]) => (type === "Partner" ? partnerLabel : "Department");
  const groups = [
    { type: "Partner" as const, items: options.filter((option) => option.type === "Partner") },
    { type: "Department" as const, items: options.filter((option) => option.type === "Department") },
  ].filter((group) => group.items.length > 0);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className={styles.linkField} ref={rootRef}>
      <span className={styles.linkFieldLabel}>
        {partnerLabel} or department{required && <span className={styles.linkRequired}> *</span>}
      </span>
      <button
        type="button"
        className={`${styles.linkTrigger} ${open ? styles.linkTriggerOpen : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className={styles.linkTriggerValue}>
            <span className={styles.linkTriggerName}>{selected.name}</span>
            <span className={styles.linkTypeTag}>{typeLabel(selected.type)}</span>
          </span>
        ) : (
          <span className={styles.linkTriggerPlaceholder}>
            {required ? `Choose a ${partnerLabel.toLowerCase()} or department` : "No link"}
          </span>
        )}
        <ChevronIcon size={14} className={open ? styles.linkChevronOpen : styles.linkChevron} />
      </button>
      {open && (
        <div className={styles.linkMenu} role="listbox">
          {!required && (
            <button
              type="button"
              role="option"
              aria-selected={value === NO_LINK}
              className={`${styles.linkOption} ${value === NO_LINK ? styles.linkOptionActive : ""}`}
              onClick={() => choose(NO_LINK)}
            >
              <span className={styles.linkOptionMuted}>No link</span>
              {value === NO_LINK && <CheckIcon />}
            </button>
          )}
          {groups.map((group) => (
            <div key={group.type} className={styles.linkGroup}>
              <span className={styles.linkGroupLabel}>{typeLabel(group.type)}</span>
              {group.items.map((option) => {
                const key = linkKey(option);
                const active = key === value;
                return (
                  <button
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`${styles.linkOption} ${active ? styles.linkOptionActive : ""}`}
                    onClick={() => choose(key)}
                  >
                    <span>{option.name}</span>
                    {active && <CheckIcon />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {!open && (
        <span className={styles.linkHint}>
          {required
            ? "Only people in this group can be invited while the portfolio is private."
            : "Optional. Linking limits private invites to people in that group."}
        </span>
      )}
    </div>
  );
};

export default LibraryLinkPicker;
